<script setup lang="ts">
import { onUnmounted, watch } from 'vue';
import { useOverlay } from '../../composables/useOverlay';
import Button from '../button/Button.vue'
const overlay = useOverlay();

const severity = [
  { name: 'success', theme: 'bg-green-100 text-green-500', icon: 'mi-check', border: '' },
  { name: 'danger', theme: 'bg-red-100 text-red-500', icon: 'mi-error' },
  { name: 'info', theme: 'bg-blue-100 text-blue-600', icon: 'mi-info' },
  { name: 'warning', theme: 'bg-yellow-200 text-yellow-400', icon: 'mi-warning' },
]

const timers = new Map<
  number,
  { timeoutId: ReturnType<typeof setTimeout>; start: number; remaining: number }
>();

function startAutoClose(id: number, duration: number) {
  stopTimer(id);
  const start = Date.now();
  const timeoutId = setTimeout(() => {
    overlay.removeToast(id);
    timers.delete(id);
  }, duration);
  timers.set(id, { timeoutId, start, remaining: duration });
}

function stopTimer(id: number) {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t.timeoutId);
    timers.delete(id);
  }
}

function pauseToast(id: number) {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t.timeoutId);
    t.remaining -= Date.now() - t.start;
    timers.set(id, { ...t, start: 0 });
  }
}

function resumeToast(id: number) {
  const t = timers.get(id);
  if (t && t.remaining > 0) {
    startAutoClose(id, t.remaining);
  }
}

function handleAction(toast: typeof overlay.toasts[number]) {
  toast.action?.handler();
  overlay.removeToast(toast.id);
  stopTimer(toast.id);
}

watch(
  () => overlay.toasts.map(t => t.id),
  (ids) => {
    ids.forEach(id => {
      const toast = overlay.toasts.find(t => t.id === id);
      if (toast && !timers.has(id)) {
        startAutoClose(id, toast.duration ?? 4000);
      }
    });
  },
  { immediate: true }
);

onUnmounted(() => {
  timers.forEach(t => clearTimeout(t.timeoutId));
  timers.clear();
});
</script>

<template>
  <div class="fixed z-2000 top-5 right-5">
    <TransitionGroup
      name="toast"
      tag="div"
    >
      <div
        v-for="toast in overlay.toasts"
        :key="toast.id"
        class="shadow-lg w-sm rounded-lg overflow-hidden mb-4"
        role="status"
        @mouseenter="pauseToast(toast.id)"
        @mouseleave="resumeToast(toast.id)"
      >
        <div class="bg-white py-2 px-3 font-medium text-base text-primary-850">
          <div class="flex items-center gap-3">
            <div
              class="flex p-2 rounded-full"
              :class="severity.find((item: any) => item.name == toast.severity)?.theme"
            >
              <i
                class="text-lg leading-0"
                :class="severity.find((item: any) => item.name == toast.severity)?.icon"
              />
            </div>
            <div class="flex-1">
              {{ toast.message }}
            </div>
            <div class="flex flex-col">
              <Button
                v-if="toast.action"
                :label="toast.action.label"
                size="sm"
                variant="outlined"
                severity="brown"
                @click="() => handleAction(toast)"
              />
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-move,
.toast-enter-active,
.toast-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}
.toast-leave-active { position: absolute; }
</style>
